import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScoreBar } from "@/components/views/companies/ArticlesTable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCompanyArticlesRange, useNewsSources } from "@/hooks/api";
import { addDays } from "@/hooks/useMonthMatrix";
import { cn } from "@/lib/utils";
import type { ArticleOut } from "@/api/client";

type SortMode = "date_desc" | "date_asc" | "trust_desc" | "trust_asc";

const SORT_LABELS: Record<SortMode, string> = {
  date_desc: "Newest first",
  date_asc: "Oldest first",
  trust_desc: "Highest trust",
  trust_asc: "Lowest trust",
};

export function EarningsArticlesTable({
  symbol,
  anchorDate,
}: {
  symbol: string;
  anchorDate: string;
}) {
  const [start, setStart] = useState<string>(() => addDays(anchorDate, -90));
  const [end, setEnd] = useState<string>(() => anchorDate);
  const [sortMode, setSortMode] = useState<SortMode>("date_desc");
  const [enabledSources, setEnabledSources] = useState<Set<number>>(new Set());

  useEffect(() => {
    setStart(addDays(anchorDate, -90));
    setEnd(anchorDate);
  }, [anchorDate]);

  const { data: sources } = useNewsSources();
  const order: "asc" | "desc" = sortMode === "date_asc" ? "asc" : "desc";
  const sourceIds = useMemo(() => Array.from(enabledSources), [enabledSources]);

  const { data: articles, isLoading } = useCompanyArticlesRange(
    symbol,
    start,
    end,
    sourceIds,
    order,
  );

  const sortedArticles = useMemo(() => {
    const arr = [...(articles ?? [])];
    if (sortMode === "trust_desc" || sortMode === "trust_asc") {
      arr.sort((a, b) => {
        const av = a.trust_score;
        const bv = b.trust_score;
        if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
        if (bv === null || bv === undefined) return -1;
        const cmp = av - bv;
        return sortMode === "trust_asc" ? cmp : -cmp;
      });
    }
    return arr;
  }, [articles, sortMode]);

  function toggleSource(id: number) {
    setEnabledSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSources() {
    setEnabledSources(new Set());
  }

  return (
    <div className="flex flex-col border-t border-border">
      <div className="px-3 py-2 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          ARTICLES · {isLoading ? "…" : (sortedArticles.length ?? 0)} found
        </div>
        <div className="w-full md:w-auto md:ml-auto flex items-center gap-1.5 flex-wrap">
          <label className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
            FROM
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="h-6 text-[11px] py-0 pl-1 pr-0.5 w-[130px] [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-datetime-edit]:p-0"
            />
          </label>
          <label className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
            TO
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="h-6 text-[11px] py-0 pl-1 pr-0.5 w-[130px] [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-datetime-edit]:p-0"
            />
          </label>

          <SortDropdown sortMode={sortMode} setSortMode={setSortMode} />

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1">
                <Filter className="h-3 w-3" />
                Sources
                {enabledSources.size > 0 && (
                  <span className="ml-1 font-mono text-[10px] bg-accent rounded px-1">
                    {enabledSources.size}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-2">
              <div className="flex items-center justify-between mb-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                <span>Filter by source</span>
                <button
                  onClick={clearSources}
                  className="hover:text-foreground"
                  disabled={enabledSources.size === 0}
                >
                  Clear
                </button>
              </div>
              <div className="max-h-[240px] overflow-y-auto space-y-1">
                {(sources ?? []).map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 text-[12px] cursor-pointer py-0.5"
                  >
                    <Checkbox
                      checked={enabledSources.has(s.id)}
                      onCheckedChange={() => toggleSource(s.id)}
                    />
                    <span className="truncate">{s.name}</span>
                  </label>
                ))}
                {(sources ?? []).length === 0 && (
                  <div className="text-[11px] text-muted-foreground">No sources available.</div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
        ) : sortedArticles.length === 0 ? (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No articles found in this date range.
          </div>
        ) : (
          <>
            <div className="md:hidden">
              {sortedArticles.map((a) => (
                <EarningsArticleMobileCard key={a.id} a={a} />
              ))}
            </div>
            <Table className="hidden md:table text-[11px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="h-7 px-2 w-[110px] text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Published
                  </TableHead>
                  <TableHead className="h-7 px-2 w-[140px] text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Origin
                  </TableHead>
                  <TableHead className="h-7 px-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Title
                  </TableHead>
                  <TableHead className="h-7 px-2 w-[80px] text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Relevance
                  </TableHead>
                  <TableHead className="h-7 px-2 w-[80px] text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Importance
                  </TableHead>
                  <TableHead className="h-7 px-2 w-[60px] text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Trust
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedArticles.map((a) => (
                  <ArticleRow key={a.id} a={a} />
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}

function ArticleRow({ a }: { a: ArticleOut }) {
  return (
    <TableRow className="hover:bg-accent/40">
      <TableCell className="py-1.5 align-top whitespace-nowrap font-mono tabular-nums text-muted-foreground text-[11px]">
        {formatShortDateTime(a.published_at ?? a.scraped_at)}
      </TableCell>
      <TableCell className="py-1.5 align-top whitespace-nowrap text-muted-foreground">
        {a.source_name}
      </TableCell>
      <TableCell className="py-1.5 align-top font-medium">
        {a.url ? (
          <a
            href={a.url}
            target="_blank"
            rel="noreferrer"
            className={cn("hover:underline")}
          >
            {a.title ?? "—"}
          </a>
        ) : (
          (a.title ?? "—")
        )}
      </TableCell>
      <TableCell className="py-1.5 align-top">
        <ScoreBar v={a.relevance_score} />
      </TableCell>
      <TableCell className="py-1.5 align-top">
        <ScoreBar v={a.importance_score} />
      </TableCell>
      <TableCell className="py-1.5 align-top font-mono tabular-nums">
        {a.trust_score ?? "—"}
      </TableCell>
    </TableRow>
  );
}

function EarningsArticleMobileCard({ a }: { a: ArticleOut }) {
  const inner = (
    <div className="block px-3 py-2 border-b border-border/60 min-h-[44px] hover:bg-accent/40">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[12px] font-medium leading-snug min-w-0 truncate">
          {a.title ?? "—"}
        </div>
        <div className="font-mono text-[10px] tabular-nums text-muted-foreground shrink-0">
          trust {a.trust_score ?? "—"}
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5 text-[10px] text-muted-foreground font-mono">
        <span className="truncate uppercase tracking-wider">{a.source_name}</span>
        <span className="shrink-0 tabular-nums">
          {formatShortDateTime(a.published_at ?? a.scraped_at)}
        </span>
      </div>
      {a.summary && (
        <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{a.summary}</div>
      )}
    </div>
  );
  if (a.url) {
    return (
      <a href={a.url} target="_blank" rel="noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return inner;
}

function SortDropdown({
  sortMode,
  setSortMode,
}: {
  sortMode: SortMode;
  setSortMode: (m: SortMode) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1">
          {SORT_LABELS[sortMode]}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[160px] p-1">
        {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => setSortMode(mode)}
            className={cn(
              "w-full text-left text-[12px] px-2 py-1 rounded hover:bg-accent",
              sortMode === mode && "bg-accent",
            )}
          >
            {SORT_LABELS[mode]}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function formatShortDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
